<template>
  <view-container>
    <browse-tabs
      browse
      :model-value="BROWSE_TABS.CONTENT"
      @toggle:filters="toggleFilterMenu"
    />

    <div class="tw-flex tw-gap-x-5">
      <filter-menu
        v-if="Settings.allowFiltering"
        :id="MY_CONTENT_ID"
        :show="filterMenuOpen"
        :tag-options="game.tag_options"
        :sort-options="CONTENT_MODS_SORT_OPTIONS"
        @update="updateTable"
      />
      <modio-table
        ref="contentTable"
        :table-id="MY_CONTENT_ID"
        :class="Classes.CONTENT"
        :row-schema="schema"
        row-id="resource_id"
        :items="modList"
        :item-total="myMods.length"
        :fetch="false"
        :limit="50"
        :loading="isLoading"
        :error="error"
        :no-data-text="
          $t('no_ugc_found', { ugc_name: ugcName(null, false, true).value })
        "
      />
    </div>
  </view-container>

  <base-modal
    v-if="Settings.allowFiltering && filterMenuOpen"
    show
    no-margin
    no-padding
    bottom
    has-close-btn
    :close-on-escape="false"
    class="sm:tw-hidden"
    @close:modal="toggleFilterMenu"
  >
    <filter-menu
      :id="MY_CONTENT_ID"
      show
      modal
      :input-loading="!game?.tag_options"
      :tag-options="game?.tag_options"
      :sort-options="CONTENT_MODS_SORT_OPTIONS"
      @update="updateTable"
    />
  </base-modal>
</template>

<script>
import { getCogsTeamsRequest, getModTeamsRequest } from '@services'
import ViewContainer from '@components/View/ViewContainer.vue'
import BrowseTabs from '@components/Navigation/BrowseTabs.vue'
import { CONTENT_MODS_SORT_OPTIONS } from '@config/sort.js'
import ModioTable from '@components/Table/ModioTable.vue'
import FilterMenu from '@components/List/FilterMenu.vue'
import { onMounted, computed, watch, ref } from 'vue'
import { Settings, GAME_ID } from '@config/game.js'
import { schema } from '@config/schemas/content.js'
import { myTeamsStore, gameStore } from '@stores'
import { BROWSE_TABS } from '@config/options.js'
import Classes from '@shared/js/config/css.js'
import { useBreakpoints } from '@vueuse/core'
import { BREAKPOINTS } from '@config'
import useFilter, {
  normalizeModFilters,
  toggleFilterMenu,
  PLATFORM_OPTION,
  filterMenuOpen,
} from '@composables/useFilter.js'
import {
  useResourceValue,
  useSearch,
  useAsync,
  useRoute,
  useSort,
} from '@composables'

const MY_CONTENT_ID = 'my-content'

export default {
  components: {
    ViewContainer,
    BrowseTabs,
    ModioTable,
    FilterMenu,
  },
  setup() {
    const { greaterOrEqual } = useBreakpoints(BREAKPOINTS)
    const smAndUp = greaterOrEqual('sm')
    const contentTable = ref(null)

    const { loading, data, error, run } = useAsync(getCogsTeamsRequest)
    const {
      loading: loadingModTeams,
      error: modTeamsError,
      data: modTeamsData,
      run: runModTeams,
    } = useAsync(getModTeamsRequest)

    const { inMemorySearch } = useSearch(MY_CONTENT_ID)
    const { ugcName } = useResourceValue()
    const { routeQuery } = useRoute()

    const { inMemoryFilter, setFilter, filter } = useFilter(MY_CONTENT_ID)

    const { setSort } = useSort(MY_CONTENT_ID, null, CONTENT_MODS_SORT_OPTIONS)

    const { setMyTeams, myMods } = myTeamsStore()
    const { getPublicGame } = gameStore()
    const game = getPublicGame()

    const isLoading = computed(() => loading.value || loadingModTeams.value)
    const modList = computed(() => {
      let data = myMods.value || []
      if (data.length) {
        data = inMemorySearch(data)
        data = inMemoryFilter(data)
      }
      return { data }
    })

    const defaultSort = CONTENT_MODS_SORT_OPTIONS.find((x) => x.default)

    setSort({
      sortBy: defaultSort.key,
      order: defaultSort.direction,
      direction: defaultSort.direction,
    })

    onMounted(fetchTeams)

    async function fetchTeams(fetchCogs = true) {
      const promises = [runModTeams()]
      if (fetchCogs) promises.push(run())

      await Promise.all(promises)
      setupFilters()

      if (
        !error.value &&
        !modTeamsError.value &&
        data.value &&
        modTeamsData.value
      ) {
        const result = modTeamsData.value.data.map((team) => ({
          ...team,
          ...(data.value.find(
            (cogsTeam) =>
              cogsTeam.resource_type === 'mod' &&
              cogsTeam.resource_id === team.id
          ) || {}),
        }))
        const game = data.value.find(
          (team) => team.resource_type === 'game' && team.game_id === GAME_ID
        )
        if (game) {
          result.push(game)
        }

        setMyTeams(result)
      }
    }

    function updateTable(_type, option) {
      if (_type === 'sort') {
        setSort({
          sortBy: option.key,
          order: option.direction,
          direction: option.direction,
        })

        contentTable.value?.updateTable('sort', option)
      }
    }

    function setupFilters() {
      const queryTags = [routeQuery.value.tags].filter((tag) => tag).join(',')

      if (!filter.value?.length || queryTags.length) {
        setFilter(
          normalizeModFilters(game.value.tag_options, {
            tags: queryTags,
          }),
          !queryTags
        )
      }
    }

    watch(smAndUp, (_smAndUp) => {
      if (Settings.value.allowFiltering && _smAndUp !== filterMenuOpen.value) {
        toggleFilterMenu()
      }
    })

    watch(filter, (_filter, _oldFilter) => {
      const platformFilter = _filter.find((x) => x.id === PLATFORM_OPTION)
        ?.selected[0]

      const oldPlatformFilter = _oldFilter.find((x) => x.id === PLATFORM_OPTION)
        ?.selected[0]

      if (platformFilter !== oldPlatformFilter) {
        fetchTeams(false)
      }
    })

    return {
      CONTENT_MODS_SORT_OPTIONS,
      toggleFilterMenu,
      filterMenuOpen,
      MY_CONTENT_ID,
      contentTable,
      updateTable,
      BROWSE_TABS,
      isLoading,
      Settings,
      Classes,
      modList,
      ugcName,
      myMods,
      schema,
      error,
      game,
    }
  },
}
</script>
